import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Github, Linkedin, Instagram, Twitter, Ghost, CheckCircle2, MessageSquare } from "lucide-react";
import { CONTACT } from "../data/portfolioData";

export default function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(formData.subject || `Inquiry from ${formData.name || "Portfolio Visitor"}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );

    window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <section id="contact" className="py-20 relative overflow-hidden bg-slate-50 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-900 text-xs font-mono font-bold shadow-sm"
          >
            <MessageSquare size={15} /> GET IN TOUCH & HIRE ME
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900"
          >
            Let’s Build Something <span className="gradient-text-indigo">Extraordinary</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-800 text-base font-medium leading-relaxed"
          >
            Available for full-time Software Engineering roles, full-stack web projects, Drupal & Python Django developments, and cloud infrastructure automation.
          </motion.p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Contact Details & Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 space-y-6"
          >
            {/* Contact Information Card */}
            <div className="p-8 rounded-3xl bg-white border border-slate-200 shadow-sm space-y-6">
              <h3 className="text-xl font-black text-slate-900">
                Contact Information
              </h3>

              <div className="space-y-4 text-sm text-slate-900">
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="flex items-center gap-4 p-3.5 rounded-2xl bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-400 transition"
                >
                  <div className="p-2.5 rounded-xl bg-indigo-100 text-indigo-700 border border-indigo-200 shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-700 font-mono font-bold">Email Address</div>
                    <div className="font-bold text-slate-900">{CONTACT.email}</div>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-3.5 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="p-2.5 rounded-xl bg-purple-100 text-purple-700 border border-purple-200 shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-700 font-mono font-bold">Phone Number</div>
                    <div className="font-bold text-slate-900">{CONTACT.phone}</div>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-3.5 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="p-2.5 rounded-xl bg-cyan-100 text-cyan-700 border border-cyan-200 shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-700 font-mono font-bold">Location</div>
                    <div className="font-bold text-slate-900">{CONTACT.location}</div>
                  </div>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="pt-4 border-t border-slate-200 space-y-3">
                <div className="text-xs font-mono font-bold text-slate-800">Social Channels:</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { icon: Github, label: "GitHub", href: CONTACT.github },
                    { icon: Linkedin, label: "LinkedIn", href: CONTACT.linkedin },
                    { icon: Instagram, label: "Instagram", href: CONTACT.instagram },
                    { icon: Twitter, label: "X", href: CONTACT.x },
                    { icon: Ghost, label: "Snapchat", href: CONTACT.snapchat },
                  ].map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2.5 rounded-xl bg-slate-100 hover:bg-indigo-600 text-slate-800 hover:text-white border border-slate-200 hover:border-indigo-500 transition-all duration-300 hover:scale-105"
                      aria-label={s.label}
                    >
                      <s.icon size={18} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Google Map Card */}
            <div className="p-4 rounded-3xl bg-white border border-slate-200 shadow-sm space-y-3">
              <div className="flex items-center justify-between text-xs font-mono font-bold text-slate-800 px-2">
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-indigo-600" /> Location Map
                </span>
                <span>Hyderabad, Telangana</span>
              </div>
              <div className="w-full h-44 rounded-2xl overflow-hidden border border-slate-200 relative bg-slate-100">
                <iframe
                  title="Hyderabad Location Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d243647.31604169542!2d78.26795793081155!3d17.412299801869818!2m3!1f0!0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb99daeaebd2c7%3A0xae93b783995bc2f2!2sHyderabad%2C%20Telangana!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                  className="w-full h-full border-0 opacity-95 hover:opacity-100 transition-opacity"
                  loading="lazy"
                />
              </div>
            </div>
          </motion.div>

          {/* Right Column: Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={handleSubmit}
              className="p-8 lg:p-10 rounded-3xl bg-white border border-slate-200 shadow-md space-y-6"
            >
              <h3 className="text-2xl font-black text-slate-900">
                Send a Direct Message
              </h3>

              <div className="grid sm:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <label className="text-xs font-mono font-bold text-slate-900">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 font-bold placeholder:text-slate-500 focus:outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 transition"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="text-xs font-mono font-bold text-slate-900">
                    Your Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 font-bold placeholder:text-slate-500 focus:outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 transition"
                  />
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <label className="text-xs font-mono font-bold text-slate-900">
                  Subject
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Software Engineer / Full Stack Opportunity"
                  className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 font-bold placeholder:text-slate-500 focus:outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 transition"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <label className="text-xs font-mono font-bold text-slate-900">
                  Message *
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Hi Rithwik, I would like to discuss..."
                  className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 font-bold placeholder:text-slate-500 focus:outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 transition resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full py-4 rounded-2xl font-black text-sm text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-600/30 hover:scale-[1.01] active:scale-[0.99] transition-all border border-indigo-500 flex items-center justify-center gap-2"
              >
                {submitted ? (
                  <>
                    <CheckCircle2 size={18} className="text-emerald-300" />
                    <span>Email App Launched!</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Send Message via Email</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
